import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orderApi } from '@/api/endpoints';
import { Alert, Empty, ErrorState, Loading } from '@/components/common/State';
import { ORDER_STATUS_LABEL, ORDER_STATUS_TONE, formatDate, formatWon } from '@/utils/format';

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const id = Number(orderId);
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderApi.detail(id),
    enabled: Number.isFinite(id),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['order', id] });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  const confirm = useMutation({
    mutationFn: () => orderApi.confirm(id),
    onSuccess: () => {
      setFeedback('구매를 확정했습니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  // 배송 전이면 즉시 취소되고, 출고 후에는 관리자 승인을 기다린다.
  const shipped = data ? data.status === 'SHIPPING' || data.status === 'DELIVERED' : false;

  const cancel = useMutation({
    mutationFn: () => orderApi.cancel(id, reason || '단순 변심'),
    onSuccess: () => {
      setFeedback(
        shipped
          ? '취소를 요청했습니다. 관리자 확인 후 처리됩니다.'
          : '주문이 취소되었습니다.',
      );
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  const refund = useMutation({
    mutationFn: () => orderApi.refund(id, reason || '단순 변심'),
    onSuccess: () => {
      setFeedback('환불을 신청했습니다. 관리자 승인 후 처리됩니다.');
      invalidate();
    },
    onError: (e: Error) => setFeedback(e.message),
  });

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState error={error} onRetry={refetch} />;
  if (!data) return <Empty title="주문을 찾을 수 없습니다." />;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h1 className="panel-title" style={{ margin: 0 }}>
          주문 상세
        </h1>
        <span className={`badge badge--${ORDER_STATUS_TONE[data.status]}`}>
          {ORDER_STATUS_LABEL[data.status]}
        </span>
      </div>

      {feedback && <Alert tone="info">{feedback}</Alert>}

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card__body">
          <table className="spec-table">
            <tbody>
              <tr>
                <th>주문번호</th>
                <td>{data.orderNo}</td>
              </tr>
              <tr>
                <th>주문일시</th>
                <td>{formatDate(data.orderedAt, true)}</td>
              </tr>
              {data.paidAt && (
                <tr>
                  <th>결제일시</th>
                  <td>{formatDate(data.paidAt, true)}</td>
                </tr>
              )}
              <tr>
                <th>받는 분</th>
                <td>
                  {data.recipient} ({data.recipientPhone})
                </td>
              </tr>
              <tr>
                <th>배송지</th>
                <td>
                  [{data.zipcode}] {data.address1} {data.address2}
                  {data.deliveryMemo && <div className="muted">메모: {data.deliveryMemo}</div>}
                </td>
              </tr>
              {data.delivery?.trackingNo && (
                <tr>
                  <th>운송장</th>
                  <td>
                    {data.delivery.courier} {data.delivery.trackingNo}
                  </td>
                </tr>
              )}
              {data.payment && (
                <tr>
                  <th>결제수단</th>
                  <td>
                    {data.payment.method ?? '-'}
                    {data.payment.receiptUrl && (
                      <>
                        {' '}
                        <a href={data.payment.receiptUrl} target="_blank" rel="noreferrer">
                          영수증
                        </a>
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <h2 style={{ fontSize: 17 }}>주문 상품</h2>
      {data.items.map((item) => (
        <div className="line-item" key={item.orderItemId}>
          <img src={item.thumbnailUrl} alt="" />
          <div className="line-item__body">
            <Link to={`/products/${item.productId}`} style={{ fontWeight: 600 }}>
              {item.productName}
            </Link>
            <p className="muted" style={{ margin: '4px 0', fontSize: 13 }}>
              {item.optionName} · {item.quantity}개
            </p>
            <strong>{formatWon(item.totalPrice)}</strong>
            {(data.status === 'DELIVERED' || data.status === 'CONFIRMED') && !item.reviewed && (
              <div style={{ marginTop: 8 }}>
                <Link
                  to={`/mypage?tab=reviews&orderItemId=${item.orderItemId}`}
                  className="btn btn--outline btn--sm"
                >
                  리뷰 쓰기
                </Link>
              </div>
            )}
          </div>
        </div>
      ))}

      <div className="card" style={{ marginTop: 24 }}>
        <div className="card__body">
          <div className="summary-row">
            <span>상품 합계</span>
            <span>{formatWon(data.totalAmount)}</span>
          </div>
          <div className="summary-row">
            <span>배송비</span>
            <span>{formatWon(data.shippingFee)}</span>
          </div>
          <div className="summary-row summary-row--total">
            <span>총 결제 금액</span>
            <span>{formatWon(data.paymentAmount)}</span>
          </div>
        </div>
      </div>

      {(data.cancelable || data.refundable || data.status === 'DELIVERED') && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card__body">
            <h2 style={{ fontSize: 16, marginTop: 0 }}>주문 관리</h2>
            {(data.cancelable || data.refundable) && (
              <div className="field">
                <label htmlFor="reason">사유</label>
                <input
                  id="reason"
                  className="input"
                  placeholder="예: 단순 변심"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {data.status === 'DELIVERED' && (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => confirm.mutate()}
                  disabled={confirm.isPending}
                >
                  구매 확정
                </button>
              )}
              {data.cancelable && (
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() => cancel.mutate()}
                  disabled={cancel.isPending}
                >
                  {shipped ? '취소 요청' : '주문 취소'}
                </button>
              )}
              {data.refundable && (
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => refund.mutate()}
                  disabled={refund.isPending}
                >
                  반품/환불 신청
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
